// src/pages/admin/AdminProductManagement.js
// CORRECTED AND UPDATED with Price History feature - June 28, 2025

import React, { useState, useEffect } from 'react';
import { db, functions } from '../../firebase';
import { collection, query, onSnapshot, orderBy, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
    Box, Button, TextField, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Modal, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle,
    CircularProgress, Alert, IconButton, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Archive as ArchiveIcon, History as HistoryIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

// --- Helper Functions for Cloud Function Calls ---
const adminCreateProduct = httpsCallable(functions, 'adminCreateProduct');
const adminUpdateProduct = httpsCallable(functions, 'adminUpdateProduct');
const adminArchiveProduct = httpsCallable(functions, 'adminArchiveProduct');


// --- Main Component ---
const AdminProductManagement = () => {
    // --- State Management ---
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Create/Edit Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentProduct, setCurrentProduct] = useState({ id: null, name: '', monthlyPrice: '' });
    const [modalError, setModalError] = useState('');

    // Archive Dialog State
    const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
    const [productToArchive, setProductToArchive] = useState(null);

    // Price History Modal State
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [priceHistory, setPriceHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedProductName, setSelectedProductName] = useState('');


    // --- Data Fetching ---
    useEffect(() => {
        const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const productsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(productsData);
            setLoading(false);
        }, (err) => {
            console.error("Error fetching products:", err);
            setError("Failed to load products. Please check console for details.");
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // --- Modal Handlers ---
    const handleOpenCreateModal = () => {
        setIsEditing(false);
        setCurrentProduct({ id: null, name: '', monthlyPrice: '' });
        setModalError('');
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (product) => {
        setIsEditing(true);
        setCurrentProduct({ id: product.id, name: product.name, monthlyPrice: product.monthlyPrice });
        setModalError('');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // --- CRUD Operations ---
    const handleSaveProduct = async () => {
        const { id, name, monthlyPrice } = currentProduct;
        const priceNumber = parseFloat(monthlyPrice);

        if (!name.trim() || !monthlyPrice || isNaN(priceNumber) || priceNumber <= 0) {
            setModalError('Please enter a valid name and a positive price.');
            return;
        }

        setModalError('');
        const toastId = toast.loading(isEditing ? "Updating product..." : "Creating product...");

        try {
            if (isEditing) {
                await adminUpdateProduct({ productId: id, name, price: priceNumber });
                toast.update(toastId, { render: "Product updated successfully!", type: "success", isLoading: false, autoClose: 5000 });
            } else {
                await adminCreateProduct({ name, price: priceNumber });
                toast.update(toastId, { render: "Product created successfully!", type: "success", isLoading: false, autoClose: 5000 });
            }
            handleCloseModal();
        } catch (err) {
            console.error(isEditing ? "Error updating product:" : "Error creating product:", err);
            const errorMessage = err.message || "An unexpected error occurred.";
            setModalError(errorMessage);
            toast.update(toastId, { render: `Error: ${errorMessage}`, type: "error", isLoading: false, autoClose: 5000 });
        }
    };

    // --- Archive Handlers ---
    const handleOpenArchiveDialog = (product) => {
        setProductToArchive(product);
        setIsArchiveDialogOpen(true);
    };

    const handleCloseArchiveDialog = () => {
        setIsArchiveDialogOpen(false);
        setProductToArchive(null);
    };

    const handleConfirmArchive = async () => {
        if (!productToArchive) return;
        const toastId = toast.loading("Archiving product...");

        try {
            await adminArchiveProduct({ productId: productToArchive.id });
            toast.update(toastId, { render: "Product archived successfully!", type: "success", isLoading: false, autoClose: 5000 });
        } catch (err) {
            console.error("Error archiving product:", err);
            toast.update(toastId, { render: `Error: ${err.message}`, type: "error", isLoading: false, autoClose: 5000 });
        } finally {
            handleCloseArchiveDialog();
        }
    };

    // --- Price History Handlers ---
    const handleViewPriceHistory = async (product) => {
        setSelectedProductName(product.name);
        setIsHistoryModalOpen(true);
        setHistoryLoading(true);
        try {
            const historyQuery = query(
                collection(db, 'products', product.id, 'priceHistory'),
                orderBy('effectiveDate', 'desc')
            );
            const querySnapshot = await getDocs(historyQuery);
            const historyData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                effectiveDate: doc.data().effectiveDate.toDate()
            }));
            setPriceHistory(historyData);
        } catch (err) {
            console.error("Error fetching price history:", err);
            toast.error("Failed to fetch price history.");
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleCloseHistoryModal = () => {
        setIsHistoryModalOpen(false);
        setPriceHistory([]);
        setSelectedProductName('');
    };


    // --- Render Logic ---
    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Paper sx={{ p: 3, m: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>Product Management</Typography>
                <Button variant="contained" onClick={handleOpenCreateModal}>Create New Product</Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Product Name</TableCell>
                            <TableCell>Monthly Price</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.map((product) => (
                            <TableRow key={product.id}>
                                <TableCell>{product.name}</TableCell>
                                <TableCell>${product.monthlyPrice?.toFixed(2)}</TableCell>
                                <TableCell>
                                    <Box sx={{
                                        color: product.status === 'Active' ? 'green' : 'orange',
                                        fontWeight: 'bold'
                                    }}>
                                        {product.status}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View Price History">
                                        <IconButton onClick={() => handleViewPriceHistory(product)}>
                                            <HistoryIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Product">
                                        <span>
                                            <IconButton onClick={() => handleOpenEditModal(product)} disabled={product.status === 'Archived'}>
                                                <EditIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Archive Product">
                                       <span>
                                            <IconButton onClick={() => handleOpenArchiveDialog(product)} disabled={product.status === 'Archived'}>
                                                <ArchiveIcon />
                                            </IconButton>
                                       </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Modal */}
            <Modal open={isModalOpen} onClose={handleCloseModal}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <Typography variant="h6" component="h2">{isEditing ? 'Edit Product' : 'Create New Product'}</Typography>
                    {modalError && <Alert severity="error" sx={{ mt: 2 }}>{modalError}</Alert>}
                    <TextField
                        fullWidth
                        label="Product Name"
                        value={currentProduct.name}
                        onChange={(e) => setCurrentProduct({ ...currentProduct, name: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Monthly Price"
                        type="number"
                        value={currentProduct.monthlyPrice}
                        onChange={(e) => setCurrentProduct({ ...currentProduct, monthlyPrice: e.target.value })}
                        margin="normal"
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveProduct}>Save</Button>
                    </Box>
                </Box>
            </Modal>

            {/* Archive Confirmation Dialog */}
            <Dialog open={isArchiveDialogOpen} onClose={handleCloseArchiveDialog}>
                <DialogTitle>Archive Product?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to archive the product "{productToArchive?.name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseArchiveDialog}>Cancel</Button>
                    <Button onClick={handleConfirmArchive} color="warning">Archive</Button>
                </DialogActions>
            </Dialog>

            {/* Price History Dialog */}
            <Dialog open={isHistoryModalOpen} onClose={handleCloseHistoryModal} maxWidth="md">
                <DialogTitle>Price History for "{selectedProductName}"</DialogTitle>
                <DialogContent>
                    {historyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : priceHistory.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Price</TableCell>
                                        <TableCell>Effective Date</TableCell>
                                        <TableCell>Set By</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {priceHistory.map((entry) => (
                                        <TableRow key={entry.id}>
                                            <TableCell>${entry.price.toFixed(2)}</TableCell>
                                            <TableCell>{entry.effectiveDate.toLocaleString()}</TableCell>
                                            <TableCell>{entry.setBy}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography sx={{ my: 3 }}>No price history found for this product.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHistoryModal}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AdminProductManagement;